/**
 * Design tokens — identidad del sitio oficial del Framework de Adopción de IA (v1.4).
 * — Blanco #FFFFFF de fondo, tinta #222222, gris secundario #6E6E6E, filetes #DDDDDD,
 *   fondo suave #F5F5F5 y carbón #3C3C3C.
 * — Amarillo #F4F177 como ÚNICO acento: solo fondos/acentos con texto oscuro encima.
 *   Nunca amarillo como texto sobre blanco (~1.1:1).
 * Los nombres de variables (`--co-*`, `--ma-*`) se conservan por retrocompatibilidad
 * con la hoja base y la mesa; cambian los valores.
 */
export const color = {
  // Funcional (selección, foco, links): tinta, no azul.
  acento: '#222222',
  acentoProfundo: '#3C3C3C',
  acentoTenue: 'rgba(34, 34, 34, 0.08)',
  acentoSutil: 'rgba(34, 34, 34, 0.04)',
  /** Anillo de foco: tinta sólida (≥3:1 sobre blanco y sobre #F5F5F5). */
  acentoAnillo: '#222222',

  texto: '#222222',
  textoTenue: '#6E6E6E',
  textoDebil: '#6E6E6E',
  textoInverso: '#FFFFFF',
  superficie: '#FFFFFF',
  superficieElevada: '#FFFFFF',
  superficieHundida: '#F5F5F5',
  fondo: '#FFFFFF',
  fondoAlt: '#F5F5F5',
  borde: '#DDDDDD',
  /** Borde de campos de formulario: ≥3:1 sobre blanco (WCAG 1.4.11). */
  bordeFuerte: '#8C8C8C',
  benchmark: '#6E6E6E',

  // Semánticos (solo estados; no compiten con el acento)
  positivo: '#1E7A4D',
  positivoTenue: '#E8F5EE',
  alerta: '#222222',
  alertaTenue: '#FFFDE6',
  peligro: '#A32017',
  peligroTenue: '#FCEDEC',

  // Escala N1→N4: grises hasta carbón; N4 resalta con el amarillo.
  n1: '#DDDDDD',
  n2: '#9A9A9A',
  n3: '#3C3C3C',
  n4: '#F4F177',
} as const;

/**
 * Capa de marca. `lima` conserva el nombre histórico pero ahora es el amarillo
 * del sitio; `negro` es la tinta #222222.
 */
export const marca = {
  lima: '#F4F177',
  amarillo: '#F4F177',
  amarilloTenue: '#FFFDE6',
  gris: '#6E6E6E',
  negro: '#222222',
  carbon: '#3C3C3C',
  blanco: '#FFFFFF',
} as const;

export const espacio = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  xxl: '40px',
  x3: '56px',
  x4: '72px',
  x5: '96px',
} as const;

export const radio = {
  chico: '6px',
  medio: '8px',
  grande: '12px',
  gigante: '16px',
  pastilla: '999px',
} as const;

export const tipografia = {
  // Sistema, sin web fonts por CDN (performance + privacidad).
  ui: "Arial, 'Liberation Sans', Helvetica, sans-serif",
  display: "Arial, 'Liberation Sans', Helvetica, sans-serif",
  numero: "Arial, 'Liberation Sans', Helvetica, sans-serif",
  mono: "'Courier New', Menlo, monospace",
} as const;

export const texto = {
  micro: '0.75rem',
  mini: '0.875rem',
  cuerpoSm: '0.9375rem',
  cuerpo: '1.0625rem',
  cuerpoLg: '1.125rem',
  titulo3: '1.125rem',
  titulo2: 'clamp(1.375rem, 1.2rem + 0.7vw, 1.75rem)',
  titulo1: 'clamp(1.75rem, 1.4rem + 1.4vw, 2.375rem)',
  display2: 'clamp(1.875rem, 1.5rem + 2vw, 2.75rem)',
  display1: 'clamp(2.125rem, 1.4rem + 3.6vw, 3.5rem)',
} as const;

export const peso = {
  // Arial solo tiene Regular y Bold: 400/500 → regular, ≥600 → bold.
  fino: '400',
  light: '400',
  normal: '400',
  medio: '500',
  semi: '700',
  fuerte: '800',
} as const;

export const tracking = {
  display: '-0.01em',
  titulo: '-0.01em',
  cuerpo: '0em',
  etiqueta: '0.06em',
  eyebrow: '0.14em',
} as const;

export const sombra = {
  nivel1: '0 1px 2px rgba(34, 34, 34, 0.06)',
  nivel2: '0 1px 2px rgba(34, 34, 34, 0.06), 0 8px 24px rgba(34, 34, 34, 0.06)',
  nivel3: '0 1px 2px rgba(34, 34, 34, 0.08), 0 12px 32px rgba(34, 34, 34, 0.1)',
  panel: '0 1px 2px rgba(34, 34, 34, 0.06), 0 8px 24px rgba(34, 34, 34, 0.1)',
  marca: 'none',
  marcaHover: 'none',
  interior: 'none',
} as const;

export const motion = {
  rapido: '140ms',
  medio: '260ms',
  entrada: '480ms',
  dato: '720ms',
  suave: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
  salida: 'cubic-bezier(0.4, 0, 0.2, 1)',
  resorte: 'cubic-bezier(0.34, 1.32, 0.64, 1)',
} as const;

function aKebab(nombre: string): string {
  return nombre.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

function emitir(prefijo: string, grupo: Record<string, string>, lineas: string[]): void {
  for (const [clave, valor] of Object.entries(grupo)) {
    lineas.push(`  --${prefijo}-${aKebab(clave)}: ${valor};`);
  }
}

export function variablesCss(): string {
  const lineas: string[] = [];
  emitir('co', color, lineas);
  emitir('ma', marca, lineas);
  // Alias con los nombres de assets/style.css del sitio del framework.
  lineas.push(`  --bg: ${color.fondo};`);
  lineas.push(`  --paper: ${color.superficie};`);
  lineas.push(`  --ink: ${color.texto};`);
  lineas.push(`  --ink2: ${color.textoTenue};`);
  lineas.push(`  --line: ${color.borde};`);
  lineas.push(`  --soft: ${color.superficieHundida};`);
  lineas.push(`  --yellow: ${marca.amarillo};`);
  lineas.push(`  --benchmark: ${color.benchmark};`);
  emitir('es', espacio, lineas);
  emitir('ra', radio, lineas);
  emitir('ti', tipografia, lineas);
  emitir('fs', texto, lineas);
  emitir('fw', peso, lineas);
  emitir('tr', tracking, lineas);
  emitir('sh', sombra, lineas);
  emitir('mo', motion, lineas);
  return `:root {\n${lineas.join('\n')}\n}`;
}

let inyectado = false;
export function inyectarTokens(): void {
  if (inyectado) return;
  inyectado = true;
  const estilo = document.createElement('style');
  estilo.id = 'cessi-tokens';
  estilo.textContent = variablesCss();
  document.head.prepend(estilo);
}
